import type { FigurinhaDaBiblioteca } from "@foto/shared";
import { LupaDeAmpliar } from "../../components/Ampliar.js";
import { Selo, cx } from "../../components/ui.js";
import { ROTULO_DO_TIPO } from "./tipos.js";

/**
 * A grade de miniaturas, sobre o xadrez: o recorte é o que se confere, e sem o xadrez uma figurinha com
 * fundo branco e uma sem fundo parecem a mesma coisa.
 *
 * Cada cartão é **um** botão (abre o detalhe) e a lupa é irmã dele, não filha: botão dentro de botão é
 * HTML inválido, e o toque na lupa abriria o detalhe junto do visor.
 */
export function GradeDeFigurinhas({
  itens,
  ehAdmin,
  desatualizada,
  aoAbrir,
  aoAmpliar,
}: {
  itens: FigurinhaDaBiblioteca[];
  ehAdmin: boolean;
  /** A lista mostrada é a da consulta anterior, enquanto a nova chega. */
  desatualizada: boolean;
  aoAbrir: (item: FigurinhaDaBiblioteca) => void;
  aoAmpliar: (item: FigurinhaDaBiblioteca) => void;
}) {
  return (
    <ul
      aria-busy={desatualizada || undefined}
      className={cx(
        "grid grid-cols-2 gap-3 transition-opacity sm:grid-cols-3 lg:grid-cols-4",
        desatualizada && "opacity-60",
      )}
    >
      {itens.map((item) => (
        <li key={item.id} className="relative">
          <button
            type="button"
            onClick={() => aoAbrir(item)}
            aria-label={`Abrir ${item.nome} — ${ROTULO_DO_TIPO[item.tipo].toLowerCase()}${item.publica ? ", pública" : ""}`}
            className="flex h-full w-full flex-col overflow-hidden rounded-peca border border-borda bg-superficie text-left transition-colors hover:border-borda-forte"
          >
            <span className="fundo-xadrez relative block aspect-square w-full">
              <img
                src={item.urls.mini}
                alt={item.nome}
                width={item.largura}
                height={item.altura}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="absolute inset-0 size-full object-contain p-2"
              />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-1.5 px-3 py-2.5">
              <span className="truncate text-[0.9375rem] text-texto">{item.nome}</span>
              <span className="flex flex-wrap gap-1">
                <Selo tom="quieto">{ROTULO_DO_TIPO[item.tipo]}</Selo>
                {item.publica && <Selo tom="fixado">Pública</Selo>}
              </span>
              {/* O admin vê as imagens de todas as casas: sem o nome do dono, não sabe de quem é qual. */}
              {ehAdmin && item.donoNome && (
                <span className="truncate text-[0.75rem] text-texto-fraco">{item.donoNome}</span>
              )}
            </span>
          </button>
          <LupaDeAmpliar
            rotulo={`Ampliar ${item.nome}`}
            aoClicar={() => aoAmpliar(item)}
            className="absolute top-0 right-0"
          />
        </li>
      ))}
    </ul>
  );
}
